import {BitmapText, Container, FederatedPointerEvent, Graphics, IBitmapTextStyle, Rectangle} from "pixi.js";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {ElevationApp} from "../../ElevationApp";
import {MdHardwarePlacement} from "../../model/hardware/MdHardware";
import {LinearDimension} from "../frameGrid/LinearDimension";
import {inches, lineBlueHeavy, lineBlueLight, lineRed} from "../../const";
import {boreHitArea, hardwareHitArea, renderHardwareBore, renderHardwareItem} from "../../shared-2d/DoorHardware";
import {HardwareGlyph, hardwareGlyphFor} from "../../shared-2d/hardwareGlyphs";
import {HardwareItem} from "../../../../../../api/Hardware";
import {hingeSideOrDefault} from "../frameProfile/Helpers";
import {clampToRule, doorPlacementViolations} from "../../model/hardware/MdHardwareRules";

// the target rectangle the crosshair positions itself within, in this Thing's
// local coords (leaf-local for a door crosshair, scene coords for a frame one).
// null when the rect isn't available yet (e.g. before the frame's first layout).
export type CrosshairRect = {x0: number; y0: number; x1: number; y1: number};

// what a crosshair measures against: the door leaf (fixed) or, for a frame
// crosshair, the jamb/head/sill stick it's mounted on (which can change as it's
// dragged). `rect()` is the current target rectangle; `rebindTo`, if present, is
// called during a drag with the crosshair's intended centre so a frame crosshair
// can re-home onto whichever perimeter stick sits under it.
export type CrosshairTarget = {
    rect: () => CrosshairRect | null;
    rebindTo?: (center: {x: number; y: number}) => void;
};

// half-length of each arm of the crosshair marker (scene units = mm).
const ARM = 2.5 * inches;

// half-size of the small square drawn at the centre of a frame crosshair (marks
// it as frame-mounted, distinct from a door crosshair).
const FRAME_SQUARE = 0.7 * inches;

// padding around the marker for its click hit area.
const HIT_PAD = 1.5 * inches;

// where the product-code label sits relative to the crosshair centre.
const LABEL_DX = 0.75 * inches;
const LABEL_DY = 0.75 * inches;

// a dimension whose span is shorter than this can't fit its number between the
// crosshair centre and the edge (common on a narrow jamb's thickness), so the
// number is pushed out past the outer edge instead.
const CRAMPED_SPAN = 4 * inches;
// how far past the outer edge a cramped dimension's number is placed.
const DIM_LABEL_OFFSET = 2.5 * inches;

const TEXT_STYLE: Partial<IBitmapTextStyle> = {
    fontName: "Arial",
    fontSize: 40,
};

// A placed hardware item positioned within a target rectangle — the door leaf or
// the frame perimeter. An item whose product category has a category glyph (see
// shared-2d/hardwareGlyphs) draws as that picture; otherwise the primary handle +
// lock (kind "handle"/"lock") draw as a lever / deadbolt and every other item
// draws as a crosshair marker. `boreOnly` reduces a handle/lock to its bored hole with no label (the
// door elevation shows the preps cut into the leaf, not the hardware on it).
// Both carry up to four edge dimensions (left/right/top/bottom) whose
// visibility follows the placement's alignToX/alignToY. The dimensions only show
// while the item is selected (or "Show All Dimensions" is on). The marker is
// draggable to reposition, and each dimension is tappable to edit. Only the
// model's x/y (offset from the rect's top-left) are stored; right/bottom derive
// from the rect size, so editing any of the four dimensions maps back to x or y.
export class HardwareCrosshairThing extends DrawFrmThing {
    private elev: ElevationApp;
    private item: MdHardwarePlacement;
    private labelText: string;
    private boreOnly: boolean;
    private categoryGlyph: HardwareGlyph | null;
    private product: HardwareItem | null;
    private mirror: () => boolean;
    private target: CrosshairTarget;
    private onSelect: (additive: boolean) => void;

    private selectedFlag = false;

    private glyph: Graphics;
    private dimsContainer: Container;
    private label: BitmapText;
    private leftDim: LinearDimension;
    private rightDim: LinearDimension;
    private topDim: LinearDimension;
    private bottomDim: LinearDimension;

    // active drag gesture: the offset from the pointer to the crosshair centre at
    // grab time, so dragging moves the centre without it jumping to the cursor.
    private gesture: {dx: number; dy: number} | null = null;
    private onGestureMove = (e: FederatedPointerEvent) => this.gestureMove(e);
    private onGestureEnd = () => this.endGesture();

    constructor(props: {
        elev: ElevationApp;
        item: MdHardwarePlacement;
        label: string;                       // product code, already truncated
        product?: HardwareItem;              // the group item, for its category glyph
        boreOnly?: boolean;                  // handle/lock draw as a plain bore, unlabelled
        // when true, the target rect is shown mirrored left-to-right (the Locations
        // view's back face): the model x stays measured from the rect's true left edge,
        // only the display flips.
        mirror?: () => boolean;
        target: CrosshairTarget;
        onSelect: (additive: boolean) => void;
    }) {
        super();
        this.elev = props.elev;
        this.item = props.item;
        this.labelText = props.label;
        this.boreOnly = !!props.boreOnly;
        this.categoryGlyph = props.product ? hardwareGlyphFor(props.product) : null;
        this.product = props.product ?? null;
        this.mirror = props.mirror ?? (() => false);
        this.target = props.target;
        this.onSelect = props.onSelect;

        // marker first, dims + label on top so the lines/labels read over it.
        this.glyph = new Graphics();
        this.dimsContainer = new Container();
        this.lines.addChild(this.glyph);
        this.lines.addChild(this.dimsContainer);

        this.label = new BitmapText(this.labelText, TEXT_STYLE);
        this.label.anchor.set(0, 1); // bottom-left anchor: sits up-right of the hub
        this.lines.addChild(this.label);

        this.leftDim = new LinearDimension(this.dimsContainer, this.elev.draw);
        this.rightDim = new LinearDimension(this.dimsContainer, this.elev.draw);
        this.topDim = new LinearDimension(this.dimsContainer, this.elev.draw);
        this.bottomDim = new LinearDimension(this.dimsContainer, this.elev.draw);

        this.makeInteractive({
            target: this.glyph,
            onClick: (e) => this.onSelect(e.ctrlKey || e.metaKey),
        });

        // press-and-drag the marker to reposition; a plain tap still selects.
        if (this.elev.readOnly) {
            this.glyph.cursor = "pointer";
        } else {
            this.glyph.cursor = "move";
            this.glyph.on("pointerdown", (e: FederatedPointerEvent) => this.beginMove(e));
        }

        // keep the highlight in sync with the canvas selection.
        this.selectedFlag = this.elev.viewSettings.isSelected(this.item.id);
        this.trackSubscription(this.elev.viewSettings.selection.subscribe(() => {
            const next = this.elev.viewSettings.isSelected(this.item.id);
            if (next === this.selectedFlag) return;
            this.selectedFlag = next;
            if (!this.destroyed) this.draw();
        }));

        // self-contained redraws: dimension-label edits and drags both userSet
        // x/y, so subscribing here means hosts never need per-item subscriptions.
        this.trackSubscription(this.item.x.subscribe(() => { if (!this.destroyed) this.draw(); }));
        this.trackSubscription(this.item.y.subscribe(() => { if (!this.destroyed) this.draw(); }));

        // show/hide dims when the "Show All Dimensions" toggle flips.
        this.trackSubscription(this.elev.viewSettings.dimShowAll.subscribe(() => {
            if (!this.destroyed) this.draw();
        }));

        // show/hide the product-code label when the export toggle flips.
        this.trackSubscription(this.elev.viewSettings.hideHardwareLabels.subscribe(() => {
            if (!this.destroyed) this.draw();
        }));

        this.draw();
    }

    getId(): string {
        return this.item.id;
    }

    // ---- drag ---------------------------------------------------------------

    private get stage() {
        return this.elev.draw.pixi.stage;
    }

    private beginMove(e: FederatedPointerEvent) {
        // ctrl/meta-click is a (multi-)select, not a drag: bail so the pointertap
        // handler's additive select runs instead of the gesture's plain select.
        if (e.ctrlKey || e.metaKey) { e.stopPropagation(); return; }

        e.stopPropagation();
        this.onSelect(false);

        this.elev.draw.setPanSuspended(true);
        this.stage.eventMode = "static";
        this.stage.on("globalpointermove", this.onGestureMove);
        window.addEventListener("pointerup", this.onGestureEnd);
        window.addEventListener("pointercancel", this.onGestureEnd);

        // record the pointer -> centre offset so the centre tracks the pointer
        // without snapping to it on grab.
        const local = this.lines.toLocal(e.global as any);
        const r = this.target.rect();
        const cx = r ? r.x0 + this.displayX(r) : local.x;
        const cy = r ? r.y0 + this.item.y.get() : local.y;
        this.gesture = {dx: cx - local.x, dy: cy - local.y};
    }

    private gestureMove(e: FederatedPointerEvent) {
        const g = this.gesture;
        if (!g || this.destroyed) return;

        // the crosshair's intended centre this frame (grab offset applied).
        const local = this.lines.toLocal(e.global as any);
        const centerX = local.x + g.dx;
        const centerY = local.y + g.dy;

        // a frame crosshair re-homes onto whichever stick sits under the centre;
        // no-op for a door crosshair. Do this before reading rect so x/y land in
        // the (possibly new) target's space.
        this.target.rebindTo?.({x: centerX, y: centerY});

        const r = this.target.rect();
        if (!r) return;
        const W = r.x1 - r.x0;
        const H = r.y1 - r.y0;

        // userSet pins the position and triggers a redraw via the x/y subscription.
        const dx = clamp(centerX - r.x0, 0, W);
        this.setPosition({x: this.mirror() ? W - dx : dx, y: clamp(centerY - r.y0, 0, H)}, "drag");
    }

    // the hub's x offset from the rect's displayed left edge: the model x, flipped when
    // the rect is shown mirrored.
    private displayX(r: CrosshairRect): number {
        const x = this.item.x.get();
        return this.mirror() ? (r.x1 - r.x0) - x : x;
    }

    // store an intended position (rect offsets), kept inside the item's placement rule and
    // that of the placement linked to it (the two move together).
    private setPosition(pos: {x: number; y: number}, debug: string) {
        let kept = this.product ? clampToRule(this.elev, this.item, this.product, pos) : pos;

        const partner = this.elev.hardware.partnerOf(this.item);
        const partnerItem = partner ? (this.elev.hardwareGroup?.hardware ?? []).find(h => h.product === partner.productId) : null;
        if(partner && partnerItem) kept = clampToRule(this.elev, partner, partnerItem, kept);

        this.item.x.userSet(kept.x, {debug: `${this.item.id}.${debug}`});
        this.item.y.userSet(kept.y, {debug: `${this.item.id}.${debug}`});
    }

    // store one axis of an intended position; the other axis stays where it is.
    private setX(x: number, debug: string) {
        this.setPosition({x, y: this.item.y.get()}, debug);
    }

    private setY(y: number, debug: string) {
        this.setPosition({x: this.item.x.get(), y}, debug);
    }

    private endGesture() {
        if (!this.gesture) return;
        this.gesture = null;
        this.stage.off("globalpointermove", this.onGestureMove);
        window.removeEventListener("pointerup", this.onGestureEnd);
        window.removeEventListener("pointercancel", this.onGestureEnd);
        this.elev.draw.setPanSuspended(false);
    }

    destroy() {
        this.endGesture();
        super.destroy();
    }

    // ---- draw ---------------------------------------------------------------

    draw() {
        if (this.destroyed) return;

        const r = this.target.rect();
        const g = this.glyph;
        g.clear();

        if (!r) {
            this.label.visible = false;
            this.hideDims();
            return;
        }

        const W = r.x1 - r.x0;
        const H = r.y1 - r.y0;
        if (W <= 0 || H <= 0) {
            this.label.visible = false;
            this.hideDims();
            return;
        }

        const ev = {debug: `${this.item.id}.center`};
        // auto-centre while unmodified so the crosshair follows the leaf/frame as
        // it resizes. Guard on value so set()'s unconditional emit doesn't recurse
        // through the x/y subscription. First drag/edit userSets -> modified ->
        // recentring stops.
        if (!this.item.x.isModified() && this.item.x.get() !== W / 2) this.item.x.set(W / 2, ev);
        if (!this.item.y.isModified() && this.item.y.get() !== H / 2) this.item.y.set(H / 2, ev);

        const mirrored = this.mirror();
        const dx = this.displayX(r);
        const cx = r.x0 + dx;
        const cy = r.y0 + this.item.y.get();

        // a placement breaking its category's placement rule draws red (marker + label).
        const invalid = !!this.product && doorPlacementViolations(this.elev, this.item, this.product, this.elev.draw.formatDimension).length > 0;

        const color = invalid ? lineRed : this.selectedFlag ? lineBlueLight : lineBlueHeavy;
        const alpha = invalid || this.selectedFlag ? 1 : 0.6;

        const primary = this.item.kind === "handle" || this.item.kind === "lock";
        const product = this.product;
        const categoryGlyph = product && this.categoryGlyph && this.categoryGlyph.targets.includes(this.item.target) ? this.categoryGlyph : null;

        // in boreOnly mode the primary handle + lock show as just their bored hole.
        if (primary && this.boreOnly) {
            renderHardwareBore(g, cx, cy, this.selectedFlag);
            g.hitArea = boreHitArea(cx, cy);
        } else if (categoryGlyph && product) {
            g.hitArea = categoryGlyph.draw(g, {
                item: product,
                hub: {x: cx, y: cy},
                target: this.item.target,
                rect: r,
                hingeSide: this.item.target === "door" ? flipSide(hingeSideOrDefault(this.elev, this.item.side === "active"), mirrored) : null,
                selected: this.selectedFlag,
                invalid,
            });
        } else if (this.item.kind === "handle" || this.item.kind === "lock") {
            // otherwise they render as their real glyph (a lever / deadbolt) rather
            // than a crosshair. `dir` is the horizontal direction toward the door
            // centre (the lever reaches inward that way): derived from which half of
            // the target rect the hub sits in.
            const dir = cx <= (r.x0 + r.x1) / 2 ? 1 : -1;
            renderHardwareItem(g, cx, cy, this.item.kind, this.selectedFlag, dir);
            g.hitArea = hardwareHitArea(cx, cy, dir);
        } else {
            g.lineStyle(1.5, color, alpha);
            g.moveTo(cx - ARM, cy); g.lineTo(cx + ARM, cy);
            g.moveTo(cx, cy - ARM); g.lineTo(cx, cy + ARM);

            // frame crosshairs get a small square at the centre so they read as
            // distinct from door crosshairs.
            if (this.item.target === "frame") {
                g.drawRect(cx - FRAME_SQUARE, cy - FRAME_SQUARE, FRAME_SQUARE * 2, FRAME_SQUARE * 2);
            }

            g.hitArea = new Rectangle(cx - ARM - HIT_PAD, cy - ARM - HIT_PAD, 2 * (ARM + HIT_PAD), 2 * (ARM + HIT_PAD));
        }

        // when selected, disambiguate the door/frame copy of the same product.
        const suffix = this.item.target === "frame" ? "Frame" : "Door";
        this.label.visible = !this.elev.viewSettings.hideHardwareLabels.value && !(primary && this.boreOnly);
        this.label.text = this.selectedFlag ? `${this.labelText} (${suffix})` : this.labelText;
        this.label.tint = color;
        this.label.position.set(cx + LABEL_DX, cy - LABEL_DY);

        const show = this.selectedFlag || !!this.elev.viewSettings.dimShowAll.value;
        if (!show) {
            this.hideDims();
            return;
        }

        const xMod = this.item.x.isModified();
        const yMod = this.item.y.isModified();

        const leftSpan = cx - r.x0;
        const rightSpan = r.x1 - cx;
        const topSpan = cy - r.y0;
        const bottomSpan = r.y1 - cy;

        // which horizontal edge dimensions to show is driven by the placement's
        // alignment: alignToX "left"/"right" shows just that horizontal dim,
        // "both" shows both. The vertical top/bottom dims always both show,
        // regardless of alignToY. alignToX names the model edges, so on a mirrored
        // rect the model-left dim is the one drawn on the displayed right.
        const showModelLeft = this.item.alignToX !== "right";
        const showModelRight = this.item.alignToX !== "left";
        const showLeft = mirrored ? showModelRight : showModelLeft;
        const showRight = mirrored ? showModelLeft : showModelRight;

        // the displayed left span is the model x (or W - x when mirrored); editing it
        // sets x back through the same flip.
        const toModelX = (displayLeft: number) => mirrored ? W - displayLeft : displayLeft;

        // left: rect left edge -> hub.
        if (showLeft) {
            this.leftDim.draw({
                axis: "horizontal", from: r.x0, to: cx, cross: cy,
                value: dx, modified: xMod,
                overrideNumberPlacement: crampedPlacement(leftSpan, r.x0, -1),
                onSet: v => this.setX(toModelX(v), "left"),
                onClear: () => this.item.x.userClearModified(),
            });
        } else {
            this.leftDim.hide();
        }

        // right: hub -> rect right edge.
        if (showRight) {
            this.rightDim.draw({
                axis: "horizontal", from: cx, to: r.x1, cross: cy,
                value: W - dx, modified: xMod,
                overrideNumberPlacement: crampedPlacement(rightSpan, r.x1, 1),
                onSet: v => this.setX(toModelX(W - v), "right"),
                onClear: () => this.item.x.userClearModified(),
            });
        } else {
            this.rightDim.hide();
        }

        // always show top/bottom
        this.topDim.draw({
            axis: "vertical", from: r.y0, to: cy, cross: cx,
            value: this.item.y.get(), modified: yMod,
            overrideNumberPlacement: crampedPlacement(topSpan, r.y0, -1),
            onSet: v => this.setY(v, "top"),
            onClear: () => this.item.y.userClearModified(),
        });

        this.bottomDim.draw({
            axis: "vertical", from: cy, to: r.y1, cross: cx,
            value: H - this.item.y.get(), modified: yMod,
            overrideNumberPlacement: crampedPlacement(bottomSpan, r.y1, 1),
            onSet: v => this.setY(H - v, "bottom"),
            onClear: () => this.item.y.userClearModified(),
        });
    }

    private hideDims() {
        this.leftDim.hide();
        this.rightDim.hide();
        this.topDim.hide();
        this.bottomDim.hide();
    }
}

function flipSide(side: "left" | "right", flip: boolean): "left" | "right" {
    if (!flip) return side;
    return side === "left" ? "right" : "left";
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

// for a dimension too short to hold its number, place the number `DIM_LABEL_OFFSET`
// past its outer edge (away from the crosshair centre) so it clears the cramped
// span. `outerEdge` is the edge coordinate away from the centre; `sign` is the
// direction to push (−1 = toward smaller coords for a left/top dim, +1 = larger
// for a right/bottom dim). Returns undefined when there's room, leaving the number
// at the span midpoint with the line broken around it.
function crampedPlacement(span: number, outerEdge: number, sign: 1 | -1): number | undefined {
    if (span >= CRAMPED_SPAN) return undefined;
    return outerEdge + sign * DIM_LABEL_OFFSET;
}
